import { Preferences } from '@capacitor/preferences';

export interface DownloadCheckpoint {
	videoId: string;
	downloadedBytes: number;
	totalSize: number;
	lastUpdate: number;
	filePath: string;
}

/**
 * HLS Download Checkpoint - for segment-based downloads (audio HLS streams)
 */
export interface HLSDownloadCheckpoint {
	audioId: string;
	downloadedSegments: number; // index of last downloaded segment (0-based)
	totalSegments: number;
	lastUpdate: number;
	filePath: string;
	segmentUrls: Array<string>; // cached segment URLs to avoid re-parsing m3u8
	hlsPlaylistUrl: string; // original m3u8 URL
}

/**
 * Save download checkpoint for resume capability
 */
export async function saveCheckpoint(videoId: string, checkpoint: DownloadCheckpoint): Promise<void> {
	try {
		await Preferences.set({
			key: `download_checkpoint_${videoId}`,
			value: JSON.stringify(checkpoint)
		});
		console.log(`💾 Checkpoint saved: ${videoId} - ${checkpoint.downloadedBytes}/${checkpoint.totalSize} bytes`);
	} catch (error) {
		console.error('Error saving checkpoint:', error);
	}
}

/**
 * Load download checkpoint
 */
export async function loadCheckpoint(videoId: string): Promise<DownloadCheckpoint | null> {
	try {
		const { value } = await Preferences.get({ key: `download_checkpoint_${videoId}` });
		if (value) {
			const checkpoint = JSON.parse(value) as DownloadCheckpoint;
			console.log(`📂 Checkpoint loaded: ${videoId} - ${checkpoint.downloadedBytes}/${checkpoint.totalSize} bytes`);
			return checkpoint;
		}
		return null;
	} catch (error) {
		console.error('Error loading checkpoint:', error);
		return null;
	}
}

/**
 * Clear download checkpoint after successful download
 */
export async function clearCheckpoint(videoId: string): Promise<void> {
	try {
		await Preferences.remove({ key: `download_checkpoint_${videoId}` });
		console.log(`🗑️ Checkpoint cleared: ${videoId}`);
	} catch (error) {
		console.error('Error clearing checkpoint:', error);
	}
}

/**
 * Get all checkpoints (for recovery on app restart)
 */
export async function getAllCheckpoints(): Promise<Array<DownloadCheckpoint>> {
	try {
		const { keys } = await Preferences.keys();
		const checkpointKeys = keys.filter(key => key.startsWith('download_checkpoint_'));
		
		const results = await Promise.all(
			checkpointKeys.map(key => Preferences.get({ key }))
		);
		
		return results
			.filter(({ value }) => value !== null)
			.map(({ value }) => JSON.parse(value!) as DownloadCheckpoint);
	} catch (error) {
		console.error('Error getting all checkpoints:', error);
		return [];
	}
}

// ============================================
// HLS Checkpoint Functions (for audio streams)
// ============================================

/**
 * Save HLS download checkpoint for resume capability
 */
export async function saveHLSCheckpoint(audioId: string, checkpoint: HLSDownloadCheckpoint): Promise<void> {
	try {
		await Preferences.set({
			key: `hls_checkpoint_${audioId}`,
			value: JSON.stringify(checkpoint)
		});
		console.log(`💾 HLS Checkpoint saved: ${audioId} - ${checkpoint.downloadedSegments}/${checkpoint.totalSegments} segments`);
	} catch (error) {
		console.error('Error saving HLS checkpoint:', error);
	}
}

/**
 * Load HLS download checkpoint
 */
export async function loadHLSCheckpoint(audioId: string): Promise<HLSDownloadCheckpoint | null> {
	try {
		const { value } = await Preferences.get({ key: `hls_checkpoint_${audioId}` });
		if (value) {
			const checkpoint = JSON.parse(value) as HLSDownloadCheckpoint;
			console.log(`📂 HLS Checkpoint loaded: ${audioId} - ${checkpoint.downloadedSegments}/${checkpoint.totalSegments} segments`);
			return checkpoint;
		}
		return null;
	} catch (error) {
		console.error('Error loading HLS checkpoint:', error);
		return null;
	}
}

/**
 * Clear HLS download checkpoint after successful download
 */
export async function clearHLSCheckpoint(audioId: string): Promise<void> {
	try {
		await Preferences.remove({ key: `hls_checkpoint_${audioId}` });
		console.log(`🗑️ HLS Checkpoint cleared: ${audioId}`);
	} catch (error) {
		console.error('Error clearing HLS checkpoint:', error);
	}
}
